import React from "react";
import { Stack, Typography, Divider, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createSupplier,
  updateSupplier,
} from "../redux/actions/supplierActions";
import { cleanFormData } from "../helpers/formHelpers";
import { SupplierRequestDto } from "../shared/dtos/supplier/SupplierRequestDto";
import { SupplierResponseDto } from "../shared/dtos/supplier/SupplierResponseDto";
import { supplierActions } from "../redux/slices/supplier";

interface CreateOrUpdateSupplierFormProps {
  supplier?: SupplierResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const supplierSchema = Yup.object().shape({
  name: Yup.string().required("Supplier name is required"),
  supplierCode: Yup.string().required("Code is required"),
  primaryPhone: Yup.string().required("Primary phone is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  address: Yup.object().shape({
    streetLine1: Yup.string().required("Street is required"),
    city: Yup.string().required("City is required"),
    country: Yup.string().required("Country is required"),
  }),
});

const CreateOrUpdateSupplierForm: React.FC<CreateOrUpdateSupplierFormProps> = ({
  supplier = null,
  isEditing = false,
  loading = false,
  error = null,
}): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const initialValues = {
    name: supplier?.name || "",
    supplierCode: supplier?.supplierCode || "",
    taxId: supplier?.taxId || "",
    primaryPhone: supplier?.primaryPhone || "",
    email: supplier?.email || "",
    website: supplier?.website || "",
    address: {
      streetLine1: supplier?.address?.streetLine1 || "",
      streetLine2: supplier?.address?.streetLine2 || "",
      city: supplier?.address?.city || "",
      stateOrProvince: supplier?.address?.stateOrProvince || "",
      postalCode: supplier?.address?.postalCode || "",
      country: supplier?.address?.country || "Kenya",
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(supplierActions.setErrorUpdate(null))
            : () => dispatch(supplierActions.setErrorCreate(null))
        }
      />
      <Formik
        initialValues={initialValues}
        validationSchema={supplierSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values) as SupplierRequestDto;
          if (isEditing && supplier) {
            dispatch(updateSupplier({ id: supplier.id, values: payload }));
          } else {
            dispatch(createSupplier(payload));
          }
        }}
        enableReinitialize
      >
        {({ dirty, isValid }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={2}>
              <Typography variant="overline" color="text.secondary">
                Company Information
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                }}
              >
                <Box sx={{ gridColumn: { xs: "1fr", sm: "span 2" } }}>
                  <FormTextField name="name" label="Company Name" />
                </Box>
                <FormTextField name="supplierCode" label="Supplier Code" />
                <FormTextField name="taxId" label="Tax ID / PIN" />
                <FormTextField name="primaryPhone" label="Primary Phone" />
                <FormTextField name="email" label="Official Email" />
                <Box sx={{ gridColumn: { xs: "1fr", sm: "span 3" } }}>
                  <FormTextField name="website" label="Website URL" />
                </Box>
              </Box>

              <Divider />
              <Typography variant="overline" color="text.secondary">
                Headquarters Address
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField
                  name="address.streetLine1"
                  label="Street Line 1"
                />
                <FormTextField
                  name="address.streetLine2"
                  label="Street Line 2"
                />
                <FormTextField name="address.city" label="City" />
                <FormTextField name="address.stateOrProvince" label="State" />
                <FormTextField name="address.country" label="Country" />
              </Box>

              <SubmitButton
                title={isEditing ? "Update Supplier" : "Register Supplier"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default CreateOrUpdateSupplierForm;
